import { computed, ComputedRef, Ref } from "vue";
import type {
  DSSelectModelValue,
  DSSelectNormalizedModelValue,
  DSSelectNormalizedOption,
  DSSelectNormalizedOptions,
  DSSelectOption,
  DSSelectOptionGroup,
  DSSelectOptions,
  DSSelectProps,
} from "@/src/design_system/form_elements/select/types/props";
import type { DSOptionValue } from "@/src/design_system/form_elements/shared/types/option";
import { DEFAULT_ITEM_TITLE_KEY, DEFAULT_ITEM_VALUE_KEY } from "@/src/design_system/form_elements/select/constants";

interface UseSelectNormalization {
  normalizedOptions: ComputedRef<DSSelectNormalizedOptions>;
  normalizedModelValue: ComputedRef<DSSelectNormalizedModelValue>;
}

export const useSelectNormalization = (
  props: Readonly<DSSelectProps>,
  modelValue: Ref<DSSelectModelValue>
): UseSelectNormalization => {
  const normalizeOption = (option: DSSelectOption): DSSelectNormalizedOption => {
    const rawOption = option as Record<string, unknown>;
    const itemTitleKey = props.itemTitle ?? DEFAULT_ITEM_TITLE_KEY;
    const itemValueKey = props.itemValue ?? DEFAULT_ITEM_VALUE_KEY;

    const mappedLabel = rawOption[itemTitleKey] as string | undefined;
    const mappedValue = rawOption[itemValueKey] as DSOptionValue | undefined;

    return {
      ...rawOption,
      label: mappedLabel ?? (rawOption.label as string | undefined),
      value: mappedValue ?? (rawOption.value as DSOptionValue | undefined) ?? null,
      rawOption: option,
    } as DSSelectNormalizedOption;
  };

  const isSelectOptionGroup = (option: DSSelectOption | DSSelectOptionGroup): option is DSSelectOptionGroup => {
    return "options" in option;
  };

  const normalizeModelValue = (value: DSSelectModelValue): DSSelectNormalizedModelValue => {
    if (!value) return null;

    if (Array.isArray(value)) {
      return value.map(normalizeOption);
    }

    return normalizeOption(value);
  };

  const toRawModelValue = (value: DSSelectNormalizedModelValue): DSSelectModelValue => {
    if (!value) return null;

    if (Array.isArray(value)) {
      return value.map(option => option.rawOption);
    }

    return value.rawOption;
  };

  const normalizedOptions = computed<DSSelectNormalizedOptions>(() => {
    const options = props.options as DSSelectOptions;

    if (!options.length) return [];

    if (isSelectOptionGroup(options[0] as DSSelectOption | DSSelectOptionGroup)) {
      return (options as DSSelectOptionGroup[]).map(group => ({
        ...group,
        options: group.options.map(normalizeOption),
      }));
    }

    return (options as DSSelectOption[]).map(normalizeOption);
  });

  const normalizedModelValue = computed<DSSelectNormalizedModelValue>({
    get: () => normalizeModelValue(modelValue.value),
    set: value => {
      modelValue.value = toRawModelValue(value);
    },
  });

  return {
    normalizedOptions,
    normalizedModelValue,
  };
};
